import { useLocation, Outlet } from 'react-router';
import { useMemo } from 'react';
import {
    LayoutGrid01,
    Users01,
    Settings01,
    File02,
    HelpCircle,
    Dataflow03,
    BookOpen01,
    BarChart01,
    Tag01,
    Package
} from '@untitledui/icons';
import { SidebarNavigationSimple } from '@/components/application/app-navigation/sidebar-navigation/sidebar-simple';
import type { NavItemType } from '@/components/application/app-navigation/config';
import { useTranslation } from 'react-i18next';

export const AdminLayout = () => {
    const location = useLocation();
    const currentPath = location.pathname;
    const { t } = useTranslation('navigation');

    // Rileva se siamo in una pagina di workflow (viewing o editing)
    const isWorkflowPage = useMemo(() => {
        return /^\/admin\/workflows\/[^/]+/.test(currentPath);
    }, [currentPath]);

    /** Sezioni a larghezza piena (niente max-w-7xl) */
    const isFullWidthSection = useMemo(
        () =>
            currentPath === "/admin/settings" ||
            currentPath.startsWith("/admin/settings/") ||
            currentPath === "/admin/documents" ||
            currentPath.startsWith("/admin/documents/") ||
            currentPath === "/admin/tags" ||
            currentPath === "/admin/containers" ||
            currentPath.startsWith("/admin/containers/"),
        [currentPath],
    );

    // Navigation items for sidebar
    const sidebarItems: NavItemType[] = [
        {
            label: t('dashboard'),
            href: '/admin/dashboard',
            icon: LayoutGrid01
        },
        {
            label: t('companies'),
            href: '/admin/companies',
            icon: Users01
        },
        {
            label: t('containers'),
            href: '/admin/containers',
            icon: Package,
            iconSrc: '/icons/ICO-container.svg',
            iconHoverSrc: '/icons/ICO-container-hover.svg',
        },
        {
            label: t('documents'),
            href: '/admin/documents',
            icon: File02
        },
        {
            label: t('workflows'),
            href: '/admin/workflows',
            icon: Dataflow03
        },
        {
            label: t('knowledgeBase'),
            href: '/admin/knowledge',
            icon: BookOpen01
        },
        {
            label: 'Tags',
            href: '/admin/tags',
            icon: Tag01
        },
        {
            label: t('analytics'),
            href: '/admin/analytics',
            icon: BarChart01
        },
        {
            label: t('settings'),
            href: '/admin/settings',
            icon: Settings01
        }
    ];

    // Footer items for sidebar
    const footerItems: NavItemType[] = [
        {
            label: t('support'),
            href: '#',
            icon: HelpCircle
        },
    ];

    const env = import.meta.env.VITE_ENV;
    const hasEnvironmentBanner = env !== "production";

    return (
        <div
            className={`flex h-screen min-h-0 min-w-0 overflow-hidden bg-secondary ${hasEnvironmentBanner ? "pt-7" : ""}`}
        >
            {/* Sidebar Navigation */}
            <SidebarNavigationSimple
                activeUrl={currentPath}
                items={sidebarItems}
                footerItems={footerItems}
                showAccountCard={true}
                className="hidden lg:flex shrink-0"
            />

            {/* Page Content — min-h-0 so flex child can shrink and scroll inside main (not on body/html) */}
            <main className="flex min-h-0 min-w-0 flex-1 overflow-y-auto overflow-x-hidden bg-secondary">
                {isWorkflowPage ? (
                    <Outlet />
                ) : (
                    <div
                        className={
                            isFullWidthSection
                                ? "mx-auto w-full min-w-0 max-w-none px-3 py-6 sm:px-4 lg:px-5"
                                : "mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8"
                        }
                    >
                        <Outlet />
                    </div>
                )}
            </main>
        </div>
    );
};
